<template>
    <div class="tw-h-full tw-flex tw-flex-col tw-space-y-3">
        <!-- Progress Indicator -->
        <div class="tw-flex-shrink-0">
            <div class="tw-flex tw-items-center tw-justify-between tw-text-sm tw-text-gray-600 tw-mb-2">
                <span>{{ $t('onboarding.progress.step_of', { current: 3, total: 4 }) }}</span>
                <span class="tw-hidden sm:tw-block">{{ $t('onboarding.progress.about_address') }}</span>
                <span class="tw-block sm:tw-hidden">{{ $t('onboarding.progress.about') }}</span>
            </div>
            <div class="tw-w-full tw-bg-gray-200 tw-rounded-full tw-h-2">
                <div class="tw-bg-primary-600 tw-h-2 tw-rounded-full tw-transition-all tw-duration-300" style="width: 75%"></div>
            </div>
        </div>

        <!-- Scrollable Content -->
        <div class="tw-flex-1 tw-overflow-y-auto tw-space-y-3">
            <!-- Introduction -->
            <div class="tw-bg-primary-50 tw-border tw-border-primary-200 tw-rounded-lg tw-p-2.5">
                <div class="tw-flex tw-items-start tw-gap-2.5">
                    <icon icon="solar:map-point-search-bold-duotone" class="tw-text-primary-600 tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
                    <div>
                        <h3 class="tw-text-sm tw-font-medium tw-text-primary-800 tw-mb-0.5">
                            {{ $t('onboarding.about_address.title') }}
                        </h3>
                        <p class="tw-text-xs tw-text-primary-700 tw-leading-tight">
                            {{ $t('onboarding.about_address.description') }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- About -->
            <FormField
                v-model="formData.about"
                type="textarea"
                :label="$t('onboarding.about_address.about_label')"
                :placeholder="$t('onboarding.about_address.about_placeholder')"
                rows="2"
                :error="errors.about"
                :hide-errors="!errors.about"
            />

            <!-- Address Section -->
            <div class="tw-space-y-2.5">
                <h3 class="tw-text-sm tw-font-medium tw-text-gray-900 tw-flex tw-items-center tw-gap-2">
                    <icon icon="solar:map-point-bold-duotone" class="tw-text-primary-600 tw-text-sm" />
                    <span class="tw-hidden sm:tw-inline">{{ $t('onboarding.about_address.address_section') }}</span>
                    <span class="tw-inline sm:tw-hidden">{{ $t('onboarding.about_address.address_section_short') }}</span>
                </h3>
                
                <!-- Street Address -->
                <FormField
                    v-model="formData.address.street"
                    type="text"
                    :label="$t('onboarding.about_address.street_address')"
                    :placeholder="$t('onboarding.about_address.street_address_placeholder')"
                    left-icon="solar:map-point-bold-duotone"
                    :error="errors.street"
                    :hide-errors="!errors.street"
                />

                <!-- City and State/Province -->
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2.5">
                    <FormField
                        v-model="formData.address.city"
                        type="text"
                        :label="$t('onboarding.about_address.city')"
                        :placeholder="$t('onboarding.about_address.city_placeholder')"
                        :error="errors.city"
                        :hide-errors="!errors.city"
                    />
                    <FormField
                        v-model="formData.address.state"
                        type="text"
                        :label="$t('onboarding.about_address.state')"
                        :placeholder="$t('onboarding.about_address.state_placeholder')"
                        :error="errors.state"
                        :hide-errors="!errors.state"
                    />
                </div>

                <!-- Country and Postal Code -->
                <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2.5">
                    <FormVueSelect
                        v-model="formData.address.country_id"
                        :options="countries"
                        :label="$t('onboarding.about_address.country')"
                        :placeholder="$t('onboarding.about_address.country_placeholder')"
                        option-value="id"
                        option-label="name"
                        left-icon="solar:map-point-bold-duotone"
                        :error="errors.country_id"
                        :hide-errors="!errors.country_id"
                    />
                    <FormField
                        v-model="formData.address.postal_code"
                        type="text"
                        :label="$t('onboarding.about_address.postal_code')"
                        :placeholder="$t('onboarding.about_address.postal_code_placeholder')"
                        :error="errors.postal_code"
                        :hide-errors="!errors.postal_code"
                    />
                </div>
            </div>

            <!-- Optional Notice -->
            <div class="tw-bg-gray-50 tw-border tw-border-gray-200 tw-rounded-lg tw-p-2">
                <div class="tw-flex tw-items-start tw-gap-2">
                    <icon icon="solar:info-circle-bold-duotone" class="tw-text-gray-500 tw-text-xs tw-mt-0.5 tw-flex-shrink-0" />
                    <p class="tw-text-xs tw-text-gray-600 tw-leading-tight">
                        {{ $t('onboarding.about_address.optional_notice') }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import FormField from '@/components/form/FormField.vue';
import FormVueSelect from '@/components/form/FormVueSelect.vue';
import { forms } from '@/api/app';

const props = defineProps({
    formData: {
        type: Object,
        required: true
    },
    errors: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['update:formData']);

// Countries data
const countries = ref([]);

// Computed form data with two-way binding
const formData = computed({
    get: () => props.formData,
    set: (value) => emit('update:formData', value)
});

// Load countries on component mount
onMounted(async () => {
    try {
        const response = await forms.countries();
        countries.value = response.data || [];
    } catch (error) {
        console.error('Error loading countries:', error);
        countries.value = [];
    }
});
</script>

<style scoped>
/* Add any specific styles if needed */
</style> 